import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import Helmet from 'react-helmet';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { PageContent } from '../../../components/UI';
import { ChangePasswordInfo, UpdateUserStoreState } from '../../../store/userV1/types';
import { EditProfileFormV1 } from '../../../components/forms';
import { Utils } from '../../../services';
import { useRoutesHelper } from '../../../helpers/routesHelper';
import { PopUp } from '../../../store/animations';
import {
  useChangePasswordMutation,
  useLazyGetCurrentAccountQuery,
  useUpdateCurrentAccountV1Mutation,
} from '../../../core/api/accountSlice';
import { useDispatch, useSelector } from '../../../configureStore';

export type IEditProfileForm = {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
  fullName: string;
  referralSlug: string;
  imageUrl: string;
  email: string;
  emailConfirmed: boolean;
};

const EditProfilePage: FC = () => {
  const { getProfileRoute } = useRoutesHelper();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isAuthorized = useSelector((state) => state.app.isAuthorized);
  const user = useSelector((state) => state.userV1);

  const [updateCurrentAccount, { isLoading: isLoadingUpdateAccount }] = useUpdateCurrentAccountV1Mutation();
  const [changePassword, { isLoading: isLoadingChangePassword }] = useChangePasswordMutation();
  const [getCurrentAccount, { isLoading: isLoadingCurrentAccount }] = useLazyGetCurrentAccountQuery();

  useEffect(() => {
    if (!isAuthorized) {
      const profileRoute = getProfileRoute();

      navigate(`${profileRoute}`, { replace: true });
      return;
    }
  }, []);

  const handleFormSubmit = async (formModel: IEditProfileForm) => {
    if (!formModel) {
      return;
    }

    const updateUserModel = new UpdateUserStoreState(formModel.fullName, formModel.referralSlug);
    const updatePasswordModel =
      !!formModel.oldPassword && !!formModel.newPassword
        ? new ChangePasswordInfo(formModel.oldPassword, formModel.newPassword)
        : null;
    const isFullNameChanged = formModel.fullName !== user.fullName;
    const isReferralSlugChanged = formModel.referralSlug !== user.referralSlug;

    if (updatePasswordModel) {
      const { currentPassword, newPassword } = updatePasswordModel;
      await changePassword({ currentPassword, newPassword });
    }
    if (isFullNameChanged || isReferralSlugChanged) {
      await updateCurrentAccount({
        fullName: updateUserModel.fullName,
        referralSlug: updateUserModel.referralSlug,
      });
    }

    const profileRoute = getProfileRoute();
    navigate(`${profileRoute}`);
  };

  return (
    <AnimatePresence>
      <PageContent center variants={PopUp} initial={'enter'} animate={'exit'}>
        <Helmet>
          <title>{Utils.toPageTitle(t('EditProfileForm.EditingProfile'))}</title>
        </Helmet>
        <Header>{t<string>('EditProfileForm.Editing')}</Header>
        {isAuthorized && (
          <EditProfileFormV1
            user={user}
            submitTitle={t('EditProfileForm.Save')}
            onSubmit={(formModel: IEditProfileForm) => handleFormSubmit(formModel)}
            isLoading={
              isLoadingUpdateAccount ||
              isLoadingChangePassword ||
              isLoadingCurrentAccount
            }
          />
        )}
      </PageContent>
    </AnimatePresence>
  );
};

const Header = styled.h1`
  font-family: ${(props) => props.theme.fonts.boldFont};
  font-size: 18px;
  text-transform: uppercase;
  text-align: center;
`;

export default EditProfilePage;
